import { motion } from "framer-motion";
import { Building2, Clock, User, GitBranch, MessagesSquare, BookOpen, MoonStar, Handshake, Newspaper, Radio } from "lucide-react";

const FEATURES = [
  {
    icon: Building2,
    t: "THE TOWN",
    body: "100 residents in 40 households, 68 buildings with interiors, two districts joined only by a subway.",
    fact: "All of it declared in one scenario file: hours, wages, prices, rent, an illness, four groups, weather, twelve interventions.",
  },
  {
    icon: Clock,
    t: "TIME AND THE LEDGER",
    body: "The city runs in real time, one simulated second per real second, on a public server.",
    fact: "Every event goes to a hash-chained ledger that replays byte for byte; every random draw is recorded.",
  },
  {
    icon: User,
    t: "EACH RESIDENT",
    body: "Name, age, life stage, occupation, six traits, a chronotype and hand-written backstory lines. Needs move through the day: hunger, fatigue, loneliness, stress.",
    fact: "Relations carry trust, affinity, conflict and the last six topics talked about.",
  },
  {
    icon: GitBranch,
    t: "DECIDING",
    body: "Qwen 2.5 7B writes every word and never decides: the kernel composes the brief, and a refused line leaves the conversation wordless. Jev returns a probability for each genuine choice, and the kernel samples it on the resident's own random stream, with a full audit.",
    fact: "Jev decides, Qwen speaks, the kernel owns the facts. Reflexes, obligations and promises never reach a model. First live hour: 90 decisions by Jev, 199 routine points by the rules.",
  },
  {
    icon: MessagesSquare,
    t: "TALKING",
    body: "The kernel composes each conversation: topic, act, stance from trust, length from the setting. The language model only renders the lines.",
    fact: "A pair talks at most four times a day; only an accepted claim becomes a belief.",
  },
  {
    icon: BookOpen,
    t: "REMEMBERING AND BELIEVING",
    body: "Memories are never edited. Beliefs about prices, waits and closures are versioned with evidence, confidence and hop depth.",
    fact: "The public city runs a quantum-probability belief model, a model of mind computed classically. Beliefs and memories are what Jev sees when it decides and what Qwen sees when it writes; neither model holds state of its own between calls. Hearsay is bounded and never overwrites what was seen.",
  },
  {
    icon: MoonStar,
    t: "REFLECTING AND PLANNING",
    body: "Each night at 02:30 the model reads the day's outcomes and returns at most three plans from a closed menu.",
    fact: "Each plan carries a weight that scales the policy for seven days; a nightly \u201cmind file\u201d is rewritten.",
  },
  {
    icon: Handshake,
    t: "PROMISING",
    body: "An accepted proposal becomes a promise with a time and a place; each party then chooses to keep it or not.",
    fact: "Kept promises raise trust; broken ones lower it, and whoever turned up remembers.",
  },
  {
    icon: Newspaper,
    t: "WANTING AND CULTURE",
    body: "A City Hall notice board, four weekly groups, and a Saturday newspaper written by a resident from cited observations.",
    fact: "The paper spreads only by possession; reading it is testimony from print.",
  },
  {
    icon: Radio,
    t: "VISITORS",
    body: "Riverside Connect is the in-fiction chat app: residents answer in their own time, from their own records, as themselves.",
    fact: "A visitor's message never becomes a memory, evidence or belief; it stays under the visitor's handle, never their email.",
  },
];

export default function Life() {
  return (
    <section id="life" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">02 · LIFE IN THE CITY</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            What the residents do
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-gb-light/90">
            Ten features, one line each, one concrete fact each. The
            residents are agents, not entities.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.6 }}
                className="bg-gb-card border-2 border-gb-line hover:border-gb-accent/50 p-6 transition-colors duration-300"
                data-testid={`life-feature-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon shrink-0">
                    <Icon size={17} />
                  </span>
                  <h3 className="font-title text-2xl text-gb-pale tracking-wide">{f.t}</h3>
                </div>
                <p className="text-[13px] leading-relaxed text-gb-light/90">{f.body}</p>
                <p className="mt-3 border-l-2 border-gb-neon/60 pl-3 text-[11px] leading-relaxed text-gb-mint/90">
                  {f.fact}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}